import type { ReactNode } from 'react'
import type { AllowRow, AllowState, FleetColumn } from '@/lib/reference'

/** yes -> no -> unset -> yes. Three states, so the cell cycles rather than toggles. */
const NEXT: Record<AllowState, AllowState> = { yes: 'no', no: 'unset', unset: 'yes' }

export interface MatrixRow {
  id: string
  /** Row label, e.g. a city pair key or a country code. */
  label: string
  /**
   * What the label addresses. A group row covers every member, so it is
   * marked: the same code can be a place and a group, and a rule that looks
   * like one station while covering 8.626 of them is the worst kind of
   * surprise in reference data.
   */
  kind?: 'station' | 'country' | 'group'
  /** Second line under the label, e.g. how many tails sit behind the verdict. */
  sublabel?: ReactNode
  /** The baseline — what this screen edits. */
  allow: AllowRow
  /**
   * Ad-hoc overrides standing against this subject, by column key.
   *
   * Shown, never edited here: the baseline is the standing rule and an
   * exception belongs to the screen it was made on. Marking it keeps the
   * matrix honest — a cell reading "yes" that something else overrides to
   * "no" would otherwise look like the last word.
   */
  exception?: AllowRow
  /** Extra detail per cell, surfaced as a title. */
  cellDetail?: (column: string) => string | undefined
}

/**
 * City pair, country or airport by aircraft, as a grid.
 *
 * A row-per-cell table would turn each fact into its own row and lose the
 * thing the operator's own sheet gets right: you read a column to see where
 * one aircraft may work, and a row to see what may work one route. The grid
 * keeps both readings.
 *
 * Columns are the group/category/type trio, and all three are drawn: type
 * alone is not an aircraft, since "CEO" covers the A320, the A321 and the
 * A330 and their limits differ.
 */
export function CapabilityMatrix({
  rowHeader,
  columns,
  rows,
  canEdit,
  onChange,
  changedIds,
}: {
  rowHeader: string
  columns: FleetColumn[]
  rows: MatrixRow[]
  canEdit: boolean
  onChange?: (id: string, allow: AllowRow) => void
  /** Rows with staged, unsaved edits. */
  changedIds?: Set<string>
}) {
  return (
    <div className="overflow-x-auto rounded-[16px] border border-hairline">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-hairline bg-surface-soft">
            <th scope="col" className="t-caption-strong px-4 py-3 align-bottom text-ink">
              {rowHeader}
            </th>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className="px-2 py-3 text-center align-bottom whitespace-nowrap"
              >
                <span className="t-caption-strong block text-ink">{column.group}</span>
                <span className="t-number-sm block text-muted">{column.category}</span>
                <span className="t-caption block text-body">{column.type}</span>
                <span className="t-caption block text-muted-soft">{column.tails}</span>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + 1}
                className="t-body-sm px-4 py-10 text-center text-muted"
              >
                No rows
              </td>
            </tr>
          )}

          {rows.map((row) => (
            <tr key={row.id} className="border-b border-hairline-soft last:border-0">
              <th scope="row" className="px-4 py-3 text-left align-middle">
                <span className="t-title-sm flex items-center gap-2 text-ink">
                  {row.label}
                  {row.kind === 'group' && (
                    <span className="t-caption-strong rounded-[100px] border border-hairline px-2 py-0.5 text-muted">
                      Group
                    </span>
                  )}
                  {changedIds?.has(row.id) && (
                    <span className="t-caption-strong rounded-[100px] bg-surface-strong px-2 py-0.5 text-muted">
                      Edited
                    </span>
                  )}
                  {row.exception && Object.keys(row.exception).length > 0 && (
                    <span className="t-caption-strong rounded-[100px] bg-ink px-2 py-0.5 text-on-dark">
                      Exception
                    </span>
                  )}
                </span>
                {row.sublabel && <span className="t-caption block text-muted">{row.sublabel}</span>}
              </th>

              {columns.map((column) => {
                const state = row.allow[column.key] ?? 'unset'
                const override = row.exception?.[column.key]
                return (
                  <td key={column.key} className="px-2 py-3 text-center align-middle">
                    <Cell
                      state={state}
                      override={override}
                      detail={row.cellDetail?.(column.key)}
                      label={`${row.label} · ${column.key}`}
                      onCycle={
                        canEdit && onChange
                          ? () => onChange(row.id, { ...row.allow, [column.key]: NEXT[state] })
                          : undefined
                      }
                    />
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const GLYPH: Record<AllowState, string> = { yes: '✓', no: '✕', unset: '—' }

function Cell({
  state,
  override,
  detail,
  label,
  onCycle,
}: {
  state: AllowState
  override?: AllowState
  detail?: string
  label: string
  onCycle?: () => void
}) {
  // DESIGN.md: semantic green and red are text colours, never fills.
  const tone =
    state === 'yes' ? 'text-semantic-up' : state === 'no' ? 'text-semantic-down' : 'text-muted-soft'
  const title = [
    state === 'unset' ? 'Not set' : undefined,
    override ? `Exception ${GLYPH[override]}` : undefined,
    detail,
  ]
    .filter(Boolean)
    .join(' · ')
  const described = override ? `${label}: ${state}, exception ${override}` : `${label}: ${state}`

  // An ink dot rather than a colour swap: the cell still shows the rule, and
  // the dot says somebody has overridden it somewhere else.
  const marker = override ? (
    <span
      aria-hidden="true"
      className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-ink"
    />
  ) : null

  if (!onCycle) {
    return (
      <span
        className={`t-title-sm relative inline-block h-8 w-8 leading-8 ${tone}`}
        title={title || undefined}
        aria-label={described}
      >
        {GLYPH[state]}
        {marker}
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={onCycle}
      title={title || undefined}
      aria-label={described}
      className={`t-title-sm relative h-8 w-8 rounded-[8px] transition-colors
                  hover:bg-surface-strong active:bg-hairline ${tone}`}
    >
      {GLYPH[state]}
      {marker}
    </button>
  )
}
